/**
 * What Settings and Login are built from besides design/primitives.tsx: the form controls (a button, a labelled
 * field, the input class) and the toasts the whole app shows. Everything in the tokens; status is a dot, never a fill.
 */
import { createContext, useCallback, useContext, useState, type ButtonHTMLAttributes, type ReactNode } from 'react';

export function cx(...c: (string | false | null | undefined)[]): string {
  return c.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------- form controls

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-n10 text-n1 hover:opacity-90',
  secondary: 'bg-n3 text-n9 hover:bg-n4 hover:text-n10',
  ghost: 'bg-transparent text-n8 hover:bg-n3 hover:text-n10',
  danger: 'bg-n3 text-fail hover:bg-n4',
};

export function Button({ variant = 'secondary', className, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button type="button" {...rest}
      className={cx('inline-flex items-center justify-center gap-2 h-9 px-3.5 rounded-chip text-small font-medium border-0 cursor-pointer whitespace-nowrap',
        'transition-colors duration-[var(--t-micro)] ease-mv disabled:opacity-40 disabled:pointer-events-none', VARIANTS[variant], className)} />
  );
}

export function Field({ label, hint, error, className, children }: { label: string; hint?: string; error?: string; className?: string; children: ReactNode }) {
  return (
    <label className={cx('flex flex-col gap-1 text-small', className)}>
      <span className="text-n8">{label}</span>
      {children}
      {(error || hint) && <span className={cx('text-micro', error ? 'text-fail' : 'text-n7')}>{error ?? hint}</span>}
    </label>
  );
}

export const inputClass = 'h-9 w-full rounded-chip bg-n3 px-3 text-small text-n10 border-0 outline-none focus-visible:ring-[1.5px] focus-visible:ring-accent placeholder:text-n7';

export function Spinner({ className }: { className?: string }) {
  return <span className={cx('inline-block size-5 rounded-full border-2 border-n4 border-t-accent animate-spin', className)} aria-label="Loading" />;
}

/** An on/off setting. The state is the colour AND the position; the label is the caller's text beside it. */
export function Switch({ checked, onChange, disabled, label }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} aria-label={label} disabled={disabled} onClick={() => onChange(!checked)}
      className={cx('relative w-9 h-5 rounded-full border-0 p-0 shrink-0 transition-colors duration-[var(--t-micro)] disabled:opacity-60 outline-none focus-visible:ring-2 focus-visible:ring-accent',
        disabled ? 'cursor-default' : 'cursor-pointer', checked ? 'bg-accent' : 'bg-n5')}>
      <span className={cx('absolute top-0.5 left-0.5 size-4 rounded-full bg-n2 transition-transform duration-[var(--t-micro)]', checked && 'translate-x-4')} />
    </button>
  );
}

// ---------------------------------------------------------------- toasts

export interface ToastAction { label: string; onClick: () => void }
interface Toast { id: number; text: string; tone: 'ok' | 'fail'; action?: ToastAction }
const ToastContext = createContext<(text: string, tone?: 'ok' | 'fail', action?: ToastAction) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const show = useCallback((text: string, tone: 'ok' | 'fail' = 'ok', action?: ToastAction) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, text, tone, action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), action ? 9000 : 4500);
  }, []);
  return (
    <ToastContext.Provider value={show}>
      {children}
      <div className="fixed bottom-5 right-5 z-[60] flex flex-col gap-2" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className="mv-pop flex items-center gap-3 rounded-card bg-n2 text-n10 shadow-float px-4 py-3 text-small max-w-sm">
            <span className={cx('size-2 rounded-full shrink-0', t.tone === 'ok' ? 'bg-ok' : 'bg-fail')} aria-hidden="true" />{t.text}
            {t.action && (
              <button type="button" className="ml-1 h-7 px-2.5 rounded-full border-0 bg-n3 text-n10 text-small font-medium cursor-pointer hover:bg-n4 shrink-0"
                onClick={() => { t.action!.onClick(); setToasts((all) => all.filter((x) => x.id !== t.id)); }}>{t.action.label}</button>
            )}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  return useContext(ToastContext);
}
